// packages/observability/src/token-redaction.ts
//
// Span and log processors that rewrite bearer tokens out of what an OTel
// record carries before it reaches the exporter: span names, span and event
// attributes, span status messages, log bodies and log attributes. Browser
// fetch and document-load spans put the page or request URL in `http.url` /
// `url.full`; Next.js server spans put the raw request path in `http.target`
// / `url.path`. Every string goes through redactTokenUrl, which only changes
// strings holding a token route or a credential query value, so the scan
// needs no list of attribute names to stay complete.
//
// Register each one AFTER any processor that stamps attributes (the context
// and baggage enrichers) and BEFORE the one that exports.

import type { AttributeValue, Attributes, Context } from "@opentelemetry/api";
import type { AnyValue } from "@opentelemetry/api-logs";
import type { LogRecordProcessor, SdkLogRecord } from "@opentelemetry/sdk-logs";
import type {
  ReadableSpan,
  Span,
  SpanProcessor,
} from "@opentelemetry/sdk-trace-base";

import { redactTokenUrl } from "./redact-token-urls";

// Log values can nest (maps inside maps); a bound keeps a cyclic or deeply
// nested meta object from costing more than a record is worth.
const MAX_DEPTH = 4;

/** `value` with tokens redacted; the same reference when nothing changed. */
function redactAttributeValue(value: AttributeValue): AttributeValue {
  if (typeof value === "string") {
    return redactTokenUrl(value);
  }
  if (!Array.isArray(value)) {
    return value;
  }
  let changed = false;
  const next = (value as Array<unknown>).map((v) => {
    if (typeof v !== "string") {
      return v;
    }
    const redacted = redactTokenUrl(v);
    if (redacted !== v) {
      changed = true;
    }
    return redacted;
  });
  return changed ? (next as AttributeValue) : value;
}

/** Rewrites, in place, every attribute whose value carries a token. */
function redactAttributesInPlace(attributes: Attributes): void {
  for (const key of Object.keys(attributes)) {
    const value = attributes[key];
    if (value === undefined) {
      continue;
    }
    const redacted = redactAttributeValue(value);
    if (redacted !== value) {
      attributes[key] = redacted;
    }
  }
}

/** `value` with tokens redacted; the same reference when nothing changed. */
function redactAnyValue(value: AnyValue, depth = 0): AnyValue {
  if (typeof value === "string") {
    return redactTokenUrl(value);
  }
  if (
    depth >= MAX_DEPTH ||
    value === null ||
    typeof value !== "object" ||
    value instanceof Uint8Array
  ) {
    return value;
  }
  let changed = false;
  const redactChild = (child: AnyValue): AnyValue => {
    const next = redactAnyValue(child, depth + 1);
    if (next !== child) {
      changed = true;
    }
    return next;
  };
  if (Array.isArray(value)) {
    const next = value.map(redactChild);
    return changed ? next : value;
  }
  const next: Record<string, AnyValue> = {};
  for (const [k, v] of Object.entries(value)) {
    next[k] = redactChild(v);
  }
  return changed ? next : value;
}

export class TokenRedactingSpanProcessor implements SpanProcessor {
  onStart(span: Span, _parentContext: Context): void {
    // A span named after its raw URL (a server request before routing
    // resolves) is renamed now: by onEnd the name is read-only.
    const name = redactTokenUrl(span.name);
    if (name !== span.name) {
      span.updateName(name);
    }
  }

  onEnd(span: ReadableSpan): void {
    // Attributes set after start (the fetch response, the resolved route)
    // only exist now. They are the span's own objects and the exporting
    // processor runs after this one, so rewriting them in place is what gets
    // exported.
    redactAttributesInPlace(span.attributes);
    for (const event of span.events) {
      if (event.attributes) {
        redactAttributesInPlace(event.attributes);
      }
    }
    if (span.status.message) {
      span.status.message = redactTokenUrl(span.status.message);
    }
  }

  async forceFlush(): Promise<void> {}
  async shutdown(): Promise<void> {}
}

export class TokenRedactingLogRecordProcessor implements LogRecordProcessor {
  onEmit(logRecord: SdkLogRecord, _context?: Context): void {
    if (logRecord.body !== undefined) {
      const body = redactAnyValue(logRecord.body);
      if (body !== logRecord.body) {
        logRecord.setBody(body);
      }
    }
    for (const [key, value] of Object.entries(logRecord.attributes)) {
      if (value === undefined) {
        continue;
      }
      const redacted = redactAnyValue(value);
      if (redacted !== value) {
        logRecord.setAttribute(key, redacted);
      }
    }
  }

  async forceFlush(): Promise<void> {}
  async shutdown(): Promise<void> {}
}
